import { memo } from 'react';
import { FormBuilderCheckbox } from '../FormBuilderCheckbox';
import { FormBuilderDate } from '../FormBuilderDate';
import { FormBuilderDropdown } from '../FormBuilderDropdown';
import { FormBuilderFile } from '../FormBuilderFile';
import { FormBuilderMultilineInput } from '../FormBuilderMultilineInput';
import { FormBuilderMultipleChoice } from '../FormBuilderMultipleChoice';
import { FormBuilderSingleChoice } from '../FormBuilderSingleChoice';
import { FormBuilderSingleLineInput } from '../FormBuilderSingleLineInput';
import { FormBuilderText } from '../FormBuilderText';
import { FormBuilderTextEditor } from '../FormBuilderTextEditor';
import { EFormBuilderFieldType, TFormBuilderField } from '@/types';

type TProps = {
  field: TFormBuilderField;
  index: number;
};

export const FormBuilderFieldFactory =
  /**
   * Factory component that renders the appropriate form builder field based on field type
   * @param {TProps} props - Component props
   * @param {TFormBuilderField} props.field - Form field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element|null} Corresponding form builder field component or null
   */
  memo(({ field, index }: TProps) => {
    switch (field.type) {
      case EFormBuilderFieldType.TEXT:
        return <FormBuilderText field={field} index={index} />;

      case EFormBuilderFieldType.CHECKBOX:
        return <FormBuilderCheckbox field={field} index={index} />;

      case EFormBuilderFieldType.SINGLE_LINE_INPUT:
        return <FormBuilderSingleLineInput field={field} index={index} />;

      case EFormBuilderFieldType.MULTILINE_INPUT:
        return <FormBuilderMultilineInput field={field} index={index} />;

      case EFormBuilderFieldType.TEXT_EDITOR:
        return <FormBuilderTextEditor field={field} index={index} />;

      case EFormBuilderFieldType.SINGLE_CHOICE:
        return <FormBuilderSingleChoice field={field} index={index} />;

      case EFormBuilderFieldType.MULTIPLE_CHOICE:
        return <FormBuilderMultipleChoice field={field} index={index} />;

      case EFormBuilderFieldType.DROPDOWN:
        return <FormBuilderDropdown field={field} index={index} />;

      case EFormBuilderFieldType.DATE:
        return <FormBuilderDate field={field} index={index} />;

      case EFormBuilderFieldType.FILE:
        return <FormBuilderFile field={field} index={index} />;

      default:
        return null;
    }
  });

FormBuilderFieldFactory.displayName = 'FormBuilderFieldFactory';
